import React, { useEffect, useMemo, useState } from 'react';
import Page from "components/page/Page";
import { useDispatch } from 'react-redux';
import { Prompt, useHistory, useParams } from 'react-router';
import { t } from 'i18next';
import { GetAudience, IAudience, IAudienceFilter, PostAudience, PutAudience, DeleteAudience } from 'api/rpc/2024-04/facilityAdmin/marketing/emailCampaigns';
import { StatusCode } from 'api/protocols';
import { showError, showSuccess } from 'redux/actions/ui';
import Card from 'components/card/Card';
import FormLayout from 'components/form/FormLayout';
import Input from 'components/form/input';
import { Badge } from "components/badge/Badge";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { ButtonNew } from 'components/buttonNew';
import _ from 'lodash';
import SelectNew, { TSelectOption } from 'components/select/SelectNew';
import AudienceCustomersTable from './AudienceCustomersTable';
import { GetCustomerType } from 'api/rpc/facilityAdmin/customerType';
import { TCustomerTag, TCustomerType } from 'redux/reducers/models/customer';
import { useUrlParams } from 'hooks/useUrlParams/useUrlParams';
import { GetMembership, TFacilityMembership } from 'api/rpc/2024-04/facilityAdmin/membership/membership';
import { GetLeague } from 'api/rpc/2024-04/facilityAdmin/league/league';
import { ILeague } from 'redux/reducers/models/league';
import { ITournament } from 'redux/reducers/models/tournament';
import { GetTournament } from 'api/rpc/2024-04/facilityAdmin/tournament/tournament';
import { GetFacility } from 'api/rpc/2024-04/facility';
import { IFacility } from 'redux/reducers/models/facility';
import { GetTag } from 'api/rpc/2024-04/facilityAdmin/client/tags';
import Sheet from 'components/sheet/Sheet';
import Popup from 'components/popup/Popup';

interface IParams {
  id?: string;
}

type TFilterFields =
  'league_roster' |
  'tournament_roster' |
  'home_facility' |
  'membership' |
  'customer_tag' |
  'address' |
  'customer_type' |
  'customer_cart_type' |
  'all_subscribers';

type TFilterOperators = 'is' | 'is_not' | 'contains' | 'does_not_contain';

export default function Audience() {
  const { searchParams } = useUrlParams({ campaign_id: '', type: '' });
  const history = useHistory();
  const dispatch = useDispatch();
  const { id } = useParams<IParams>();
  const isEdit = id != null;
  const [audience, setAudience] = useState<IAudience>({
    id: 0,
    type: searchParams.get('type') as 'static' | 'smart',
    title: '',
    filters: [],
    included_customer_count: 0,
    total_customer_count: 0,
    created_at: '',
    updated_at: '',
  });
  const filters = audience.filters;
  const fields: TFilterFields[] = [
    'league_roster',
    'tournament_roster',
    'home_facility',
    'membership',
    'customer_tag',
    'customer_type',
    'customer_cart_type',
    'all_subscribers',
  ];
  const operators: TFilterOperators[] = [
    'contains',
    'does_not_contain',
  ];
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ [key: string]: string[] }>({});

  const [openAudienceTypeModal, setOpenAudienceTypeModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  useEffect(() => {
    function handleBeforeUnload(event: BeforeUnloadEvent) {
      if (hasChanges && isEdit) {
        event.preventDefault();
        return "";
      }

      return undefined;
    }

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasChanges]);

  useEffect(() => {
    void loadFilterValues();

    if (!isEdit) {
      void create();
    } else {
      void loadAudience();
    }
  }, []);

  useEffect(() => {

    if (isEdit && openAudienceTypeModal) {
      void update();
    }
  }, [audience.type]);

  const [customerCartTypes, setCustomerCartTypes] = useState<TCustomerType[]>([]);
  const [customerTypes, setCustomerTypes] = useState<TCustomerType[]>([]);
  const [memberships, setMemberships] = useState<TFacilityMembership[]>([]);
  const [leagues, setLeagues] = useState<ILeague[]>([]);
  const [tournaments, setTournaments] = useState<ITournament[]>([]);
  const [facilities, setFacilities] = useState<IFacility[]>([]);
  const [customerTags, setCustomerTags] = useState<TCustomerTag[]>([]);

  async function loadFilterValues() {
    const tasks = [];

    tasks.push(GetCustomerType({}).then(resp => {
      const data: TCustomerType[] = resp.data;
      setCustomerTypes(data.filter(t => t.application === 'green_fee'));
      setCustomerCartTypes(data.filter(t => t.application === 'power_cart'));
    }));

    tasks.push(GetMembership({ archived: false }, false).then(resp => setMemberships(resp.data)));
    tasks.push(GetLeague({}, false).then(resp => setLeagues(resp.data)));
    tasks.push(GetTournament({}, false).then(resp => setTournaments(resp.data)));
    tasks.push(GetFacility('', false).then(resp => setFacilities(resp.data)));
    tasks.push(GetTag({ type: 'customer' }, false).then(resp => setCustomerTags(resp.data)));

    await Promise.all(tasks);
  }

  async function loadAudience() {
    const resp = await GetAudience({ id }, false);

    if (resp.status !== StatusCode.OK) {
      dispatch(showError(resp.message));
      return;
    }

    console.log(resp.data, 'loaded audience');
    setAudience(resp.data);
  }

  function save() {
    if (isEdit) {
      void update();
    } else {
      void create();
    }
  }

  async function update(): Promise<boolean> {
    const resp = await PutAudience({
      ...audience,
    }, true);

    if (resp.status !== StatusCode.OK) {
      const errors: { [key: string]: string[] } = resp.data as any;
      const firstErrorKey = Object.keys(errors)[0];
      const firstErrorMessage = errors[firstErrorKey];
      dispatch(showError(firstErrorMessage[0] ?? ''));
      setErrors(errors);
      return false;
    }

    dispatch(showSuccess(t('Updated smart list')));

    setAudience(resp.data);
    setHasChanges(false);
    setOpenAudienceTypeModal(false);
    setErrors({});
    return true;
  }

  async function create() {
    const resp = await PostAudience({
      ...audience,
      filters: filters,
      title: audience.title == '' ? 'New Smart List' : audience.title,
    }, false);

    if (resp.status !== StatusCode.OK) {
      dispatch(showError(resp.message));
      return;
    }

    setAudience(resp.data.data);
    setHasChanges(false);

    dispatch(showSuccess(t('Created audience')));
    history.push('/admin/email-marketing/campaigns/smart-list/' + resp.data.data.id + '?' + searchParams.toString());
  }

  async function deleteAudience() {
    const resp = await DeleteAudience({ id: audience.id }, true);

    if (resp.status !== StatusCode.OK) {
      dispatch(showError(typeof resp.data === 'string' ? resp.data : t('Error deleting list')));
      setOpenDeleteModal(false);
      return;
    }

    dispatch(showSuccess(t('Deleted list')));
    history.push('/admin/email-marketing');
  }

  function setFilters(newFilters: IAudience['filters']) {
    setHasChanges(true);
    setCustomerTableState({
      ...customerTableState,
      offset: 0,
    });
    setAudience({
      ...audience,
      filters: newFilters,
    });
  }

  function addFilter() {
    setFilters(filters.concat([{
      field: '',
      operator: 'contains',
      value: [],
    }]));
  }

  function removeFilter(i: number) {
    setFilters(filters.slice(0, i).concat(filters.slice(i + 1)));
  }

  function handleSelectedField(i: number, value: string) {
    const filter = filters[i];
    const newFilter: IAudienceFilter = {
      ...filter,
      field: value,

      // When switching away from all_subscribers, reset to empty defaults.
      ...(value === 'all_subscribers'
        ? { operator: 'is', value: ['true'] }
        : filter.field === 'all_subscribers'
          ? { operator: 'contains', value: [] }
          : {}),
    };
    const updatedFilters = filters.slice(0, i)
      .concat([newFilter])
      .concat(filters.slice(i + 1));

    setFilters(updatedFilters);

    removeErrorState(`filters.${i}.field`);
  }

  function handleSelectedOperator(i: number, value: string) {
    const filter = filters[i];
    const newFilter: IAudienceFilter = {
      ...filter,
      operator: value,
    };
    const updatedFilters = filters.slice(0, i)
      .concat([newFilter])
      .concat(filters.slice(i + 1));

    setFilters(updatedFilters);

    removeErrorState(`filters.${i}.operator`);
  }

  function handleFilterValueSelected(i: number, value: string | number) {
    const filter = filters[i];
    const valueIndex = filter.value.indexOf(value);
    let filterValues: (string | number)[] = [];

    if (valueIndex >= 0) {
      filterValues = filter.value.slice(0, valueIndex).concat(filter.value.slice(valueIndex + 1));
    } else {
      filterValues = filter.value.concat([value]);
    }
    const newFilter: IAudienceFilter = {
      ...filter,
      value: filterValues,
    };
    const updatedFilters = filters.slice(0, i).concat([newFilter]).concat(filters.slice(i + 1));
    setFilters(updatedFilters);

    removeErrorState(`filters.${i}.value`);
  }

  function removeErrorState(errorKey: string) {
    const updatedErrors = {
      ...errors,
    };
    delete updatedErrors[errorKey];
    setErrors(updatedErrors);
  }

  const [customerTableState, setCustomerTableState] = useState({
    limit: 15,
    offset: 0,
    search: '',
  });


  function filterValuePlaceholder(filter: IAudienceFilter): string {
    let placeholder = t('filter by...');

    switch (filter.field as (TFilterFields | '')) {
      case 'membership':
        placeholder = memberships.filter(m => filter.value.includes(m.id)).map<string>(m => m.title).join(', ');
        break;
      case 'customer_type':
        placeholder = customerTypes.filter(c => filter.value.includes(c.id)).map(c => c.title).join(', ');
        break;
      case 'customer_cart_type':
        placeholder = customerCartTypes.filter(c => filter.value.includes(c.id)).map(c => c.title).join(', ');
        break;
      case 'league_roster':
        placeholder = leagues.filter(l => filter.value.includes(l.id)).map(l => l.name).join(', ');
        break;
      case 'tournament_roster':
        placeholder = tournaments.filter(t => filter.value.includes(t.id)).map(t => t.name).join(', ');
        break;
      case 'home_facility':
        placeholder = facilities.filter(f => filter.value.includes(f.id)).map(f => f.long_name).join(', ');
        break;
      case 'customer_tag':
        placeholder = customerTags.filter(c => filter.value.includes(c.id)).map(c => c.label).join(', ');
        break;
      case '':
        break;
      default:
        console.error('unsupported filter field: ' + filter.field);
    }

    return placeholder.length > 0 ? placeholder : t('filter by...');
  }

  function filterValueOptions(filter: IAudienceFilter) {
    let options: TSelectOption<IAudienceFilter>[] = [];

    switch (filter.field as (TFilterFields | '')) {
      case 'membership':
        options = memberships.map(m => ({
          id: m.id,
          label: m.title,
        }));
        break;
      case 'customer_type':
        options = customerTypes.map(t => ({
          id: t.id,
          label: t.title,
        }));
        break;
      case 'customer_cart_type':
        options = customerCartTypes.map(t => ({
          id: t.id,
          label: t.title,
        }));
        break;
      case 'league_roster':
        options = leagues.map(l => ({
          id: l.id,
          label: l.name,
        }));
        break;
      case 'tournament_roster':
        options = tournaments.map(l => ({
          id: l.id,
          label: l.name,
        }));
        break;
      case 'home_facility':
        options = facilities.map(l => ({
          id: l.id,
          label: l.long_name,
        }));
        break;
      case 'customer_tag':
        options = customerTags.map(l => ({
          id: l.id,
          label: l.label,
        }));
        break;
      case '':
        break;

      default:
        console.error('unsupported filter value: ' + filter.field);
    }

    return options;
  }

  function changeListType() {
    setAudience(a => ({ ...a, type: audience.type === 'smart' ? 'static' : 'smart', filters: [] }));
  }

  const typeTitle = useMemo(() => audience.type === 'static' ? t('Static') : t('Smart'), [audience.type]);

  return <Page
    narrow
    title={isEdit ? t(`Edit ${typeTitle} List`) : t(`Create New ${typeTitle} List`)}
    subtitle={t('Create a reusable list of customers for future campaigns.')}
    primaryAction={{
      action: save,
      content: t('Save'),
    }}
    multipleActionDropdownAction={{
      label: t('Options'),
      dropdownProps: {
        alignment: 'right',
        options: [
          {
            type: 'handler',
            handler: () => setOpenAudienceTypeModal(true),
            label: t(audience.type === 'static' ? `Convert to smart list` : 'Convert to static list'),
            icon: audience.type === 'static' ? 'magnifying-glass' : 'file-download',
          },
          ...(isEdit ? [
            {
              type: 'handler' as const,
              handler: () => setOpenDeleteModal(true),
              label: t('Delete list'),
              icon: 'trash',
            },
          ] : []),
        ],
      },
    }}
    breadcrumbs={
      !searchParams.has('campaign_id') || searchParams.get('campaign_id') == ''
        ? [{
          label: t('Email Marketing'),
          url: '/admin/email-marketing',
        }, {

          label: t('Edit Smart List'),
        }]
        : [{
          prefix: true,
          label: t('Back to campaign builder'),
          url: '/admin/email-marketing/campaigns/' + searchParams.get('campaign_id') + '/new?step=2',
        }]
    }
  >
    <Prompt
      when={hasChanges}
      message={t('You still have unsaved changes. Continuing will discard all your updates. Are you sure you wish to continue?')}
    />

    <Card>
      <Card.Section>
        <FormLayout>
          <FormLayout.Group>
            <div className="mt-2">
              <Input
                label={t("Name your list")}
                placeholder={t("i.e. Members from British Columbia, etc.")}
                value={audience.title}
                onChange={e => {
                  setAudience({ ...audience, title: e.target.value });
                  setHasChanges(true);
                }}
              />
            </div>
          </FormLayout.Group>
        </FormLayout>
      </Card.Section>

      {audience.type === 'smart' &&
        <Card.Section>
          <div className="text-sm font-medium text-gray-700 mb-2 lg:mb-4">
            {t('Filter by')}:
          </div>
          <FormLayout>
            {filters.map((filter, i) => <>
              {i !== 0 &&
                <FormLayout.Group key={'filter-and-' + i.toString()}>
                  <div className="flex">
                    <Badge type="primary">
                      {t('And')}
                    </Badge>
                  </div>
                </FormLayout.Group>
              }
              <FormLayout.Group key={'filter-group-' + i.toString()}>
                <div className="grid grid-cols-[260px_150px_minmax(150px,_1fr)_70px] gap-6 items-center">
                  <SelectNew
                    id={`filter-field-${i}`}
                    placeholder={t('Filter by...  ')}
                    value={filter.field}
                    onChange={val => handleSelectedField(i, val)}
                    options={fields.map(field => ({
                      id: field,
                      label: _.startCase(field),
                    }))}
                    error={Object.keys(errors).includes(`filters.${i}.field`)}
                    searchable={100}
                  />
                  {filter.field !== 'all_subscribers' && <>
                    <SelectNew
                      id={`filter-operator-${i}`}
                      value={filter.operator}
                      onChange={val => handleSelectedOperator(i, val)}
                      options={operators.map(operator => ({
                        id: operator,
                        label: _.startCase(operator),
                      }))}
                      error={Object.keys(errors).includes(`filters.${i}.operator`)}
                    />

                    <SelectNew
                      id={`filter-value-${i}`}
                      placeholder={filterValuePlaceholder(filter)}
                      multiValue={filter.value}
                      onChange={val => {
                        handleFilterValueSelected(i, val);
                      }}
                      options={filterValueOptions(filter)}
                      error={Object.keys(errors).includes(`filters.${i}.value`)}
                      searchable={100}
                    />
                  </>}
                  <ButtonNew type="tertiary" className="px-0" onClick={() => removeFilter(i)}>
                    {t('Remove')}
                  </ButtonNew>
                </div>
              </FormLayout.Group>
            </>,
            )}
            <FormLayout.Group key="add-filter-row">
              <ButtonNew type="tertiary" className="px-0" onClick={addFilter}>
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                {t('Add Filter')}
              </ButtonNew>
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>
      }
      <Card.Section>
        <AudienceCustomersTable
          audience={audience}
          onImport={audience.type === 'static' ? () => void loadAudience() : null}
          onSave={isEdit ? () => update() : null}
          selectable={true}
        />
      </Card.Section>
    </Card>

    <ChangeAudienceTypeModal
      open={openAudienceTypeModal}
      onOk={changeListType}
      audience={audience}
    />

    <Popup
      open={openDeleteModal}
      okText={t('Delete List')}
      onOk={deleteAudience}
      onCancel={() => setOpenDeleteModal(false)}
      title={t('Delete List')}
      type="warning"
      description={t(`Are you sure you want to delete this list? This action cannot be undone.`)}
    />
  </Page >;
}

function ChangeAudienceTypeModal({
  open,
  onOk,
  audience,
}: {
  open: boolean
  onOk: () => void
  audience: IAudience
}) {
  const [isOpen, setIsOpen] = useState(open);

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  return <Sheet
    open={isOpen}
    okText={t('Convert List')}
    onOk={onOk}
    onCancel={() => setIsOpen(false)}
    title={t(
      audience.type === 'static'
        ? 'Convert to smart list'
        : 'Convert to static list',
    )}
  >
    <div className="text-md gap-4">
      <div>{t('Converting this list will reset all the users in the list.')}</div>
      <div>{t('Are you sure you want to continue?')}</div>
    </div>
  </Sheet>;

}
